// The scan service: every engine scan the app runs goes through this one
// queue, so the automatic scan and the opening history never compete for the
// engine and the importer's button shows one progress. A game is scanned
// once; a game already complete is never queued. The automatic scan
// (`scanAll`) covers the last 30 days, the window the whole app covers
// (`inPeriod`, the window the game store holds), so an older game is never
// scanned.

import { inPeriod } from '@chess/api-model';
import { DEFAULT_SECONDS_PER_GAME, type GameScanState, type ScanProgress, type ScanService, type ScanSource } from './types.ts';

/** What the queue needs from the games and analysis APIs. */
export interface ScanSources {
  /** The games, newest first: id and date; the store's, which holds only the 30-day window. */
  list(): Promise<ReadonlyArray<{ id: string; playedAt: string }>>;
  /** Whether the game's stored scan is complete. */
  isComplete(id: string): Promise<boolean>;
  /** Runs or continues the game's scan; resolves when it is complete. */
  scan(id: string): Promise<unknown>;
  /** Hears scans completed outside the queue (the open review's own). */
  onComplete?(listener: (id: string) => void): () => void;
  /** The clock for timing scans; `Date.now` by default. */
  now?(): number;
  /** The time the 30-day window ends; `Date.now` by default. */
  today?(): number;
  warn?(message: string, data: Record<string, unknown>): void;
}

interface Waiting {
  id: string;
  source: ScanSource;
}

/** How many stored scans are read at once while counting. */
const READ_AT_ONCE = 16;

export class ScanQueue implements ScanService {
  private readonly sources: ScanSources;
  private readonly listeners = new Set<() => void>();
  /** Known completeness by game, read once, then kept by the queue and the completion events. */
  private readonly complete = new Map<string, boolean>();
  private readonly states = new Map<string, GameScanState>();
  private readonly paused = new Set<ScanSource>();
  private waiting: Waiting[] = [];
  private current: ScanProgress = { running: false, done: 0, total: 0, stopping: false };
  private draining = false;
  private timed = { games: 0, seconds: 0 };
  /** The automatic pass reading now, and whether another was asked meanwhile. */
  private passing?: Promise<void>;
  private passAgain = false;

  constructor(sources: ScanSources) {
    this.sources = sources;
    sources.onComplete?.((id) => {
      if (this.complete.get(id) === true) return;
      this.complete.set(id, true);
      this.notify();
    });
  }

  progress(): ScanProgress {
    return this.current;
  }

  subscribe(listener: () => void): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  scanAll(): Promise<void> {
    if (this.passing) {
      this.passAgain = true;
      return this.passing;
    }
    this.passing = (async () => {
      try {
        do {
          this.passAgain = false;
          const ids = await this.windowIds();
          await this.readCompleteness(ids);
          // A failed game is not retried here, or a game the engine cannot
          // finish would be queued again on every change; the next load retries it.
          this.enqueue(ids.filter((id) => !this.complete.get(id) && !this.states.has(id)), AUTO);
          this.orderAuto(ids);
        } while (this.passAgain);
      } finally {
        this.passing = undefined;
      }
    })();
    return this.passing;
  }

  enqueue(ids: readonly string[], source: ScanSource): void {
    let added = 0;
    for (const id of ids) {
      if (this.complete.get(id) === true || this.states.has(id)) continue;
      this.waiting.push({ id, source });
      this.states.set(id, 'queued');
      added += 1;
    }
    if (added === 0) return;
    this.set({ total: this.current.total + added });
    void this.drain();
  }

  forget(id: string): void {
    this.complete.delete(id);
    if (this.states.get(id) === 'scanning') return;
    this.states.delete(id);
    const at = this.waiting.findIndex((w) => w.id === id);
    if (at >= 0) {
      this.waiting.splice(at, 1);
      this.set({ total: this.current.total - 1 });
    }
  }

  prioritize(id: string, source: ScanSource): void {
    if (this.complete.get(id) === true || this.states.get(id) === 'scanning') return;
    const at = this.waiting.findIndex((w) => w.id === id);
    if (at >= 0) this.waiting.splice(at, 1);
    else this.set({ total: this.current.total + 1 });
    this.waiting.unshift({ id, source });
    this.states.set(id, 'queued');
    this.notify();
    void this.drain();
  }

  state(id: string): GameScanState | undefined {
    return this.states.get(id);
  }

  pause(source: ScanSource): void {
    this.paused.add(source);
    this.notify();
  }

  resume(source: ScanSource): void {
    if (!this.paused.delete(source)) return;
    this.notify();
    void this.drain();
  }

  stop(): void {
    for (const w of this.waiting) this.states.delete(w.id);
    this.waiting = [];
    if (this.current.running) this.set({ stopping: true, total: this.current.done + 1 });
    else this.set({ done: 0, total: 0 });
  }

  secondsPerGame(): number {
    return this.timed.games > 0 ? this.timed.seconds / this.timed.games : DEFAULT_SECONDS_PER_GAME;
  }

  /** The next waiting game whose source is not paused. */
  private take(): Waiting | undefined {
    const at = this.waiting.findIndex((w) => !this.paused.has(w.source));
    return at < 0 ? undefined : this.waiting.splice(at, 1)[0];
  }

  private async drain(): Promise<void> {
    if (this.draining) return;
    this.draining = true;
    try {
      for (let next = this.take(); next; next = this.take()) {
        const { id } = next;
        this.states.set(id, 'scanning');
        this.set({ running: true, current: id });
        const started = this.now();
        try {
          await this.sources.scan(id);
          this.states.delete(id);
          this.complete.set(id, true);
          this.timed = { games: this.timed.games + 1, seconds: this.timed.seconds + (this.now() - started) / 1000 };
        } catch (error) {
          this.states.set(id, 'failed');
          this.sources.warn?.('scan did not complete', { id, error: String(error) });
        }
        this.set({ done: this.current.done + 1 });
        if (this.current.stopping) break;
      }
    } finally {
      this.draining = false;
      // Paused games stay queued and count again when their source resumes.
      const held = this.waiting.length;
      this.set({ running: false, current: undefined, stopping: false, done: 0, total: held });
    }
  }

  /**
   * Puts the automatic scan's waiting games in the window's order, newest
   * first, whatever order they arrived in: an import adds games oldest first,
   * one pass per game, and first come would scan the latest game last, when
   * it is the one the home shows. Other sources' games keep their places.
   */
  private orderAuto(ids: readonly string[]): void {
    const rank = new Map(ids.map((id, i) => [id, i]));
    const autos = this.waiting.filter((w) => w.source === AUTO).sort((a, b) => (rank.get(a.id) ?? Infinity) - (rank.get(b.id) ?? Infinity));
    if (autos.length < 2) return;
    let k = 0;
    this.waiting = this.waiting.map((w) => (w.source === AUTO ? autos[k++] : w));
  }

  /**
   * The games inside the 30-day window, newest first. The window is checked
   * here as well as by the source (the store holds only the window), so the
   * queue can never reach past it whatever the source returns.
   */
  private async windowIds(): Promise<string[]> {
    const today = this.sources.today?.() ?? Date.now();
    const games = await this.sources.list();
    return games.filter((g) => inPeriod(Date.parse(g.playedAt), '30d', today)).map((g) => g.id);
  }

  private async readCompleteness(ids: readonly string[]): Promise<void> {
    const unknown = ids.filter((id) => !this.complete.has(id));
    for (let i = 0; i < unknown.length; i += READ_AT_ONCE) {
      const slice = unknown.slice(i, i + READ_AT_ONCE);
      const known = await Promise.all(slice.map((id) => this.sources.isComplete(id).catch(() => false)));
      slice.forEach((id, j) => {
        // A scan that finished while reading wins over the read.
        if (this.complete.get(id) !== true) this.complete.set(id, known[j]);
      });
    }
  }

  private now(): number {
    return this.sources.now?.() ?? Date.now();
  }

  private set(change: Partial<ScanProgress>): void {
    this.current = { ...this.current, ...change };
    this.notify();
  }

  private notify(): void {
    for (const listener of this.listeners) listener();
  }
}

const AUTO = 'auto' as ScanSource;

/** "~15 min", "~40 s": the time the games left would take, rounded for display. */
export function estimate(games: number, secondsPerGame: number): string {
  const seconds = games * secondsPerGame;
  if (seconds < 60) return `~${Math.max(5, Math.round(seconds / 5) * 5)} s`;
  const minutes = Math.round(seconds / 60);
  if (minutes < 60) return `~${minutes} min`;
  const hours = Math.floor(minutes / 60);
  const rest = minutes % 60;
  return rest ? `~${hours} h ${rest} min` : `~${hours} h`;
}
